import { createTheme, getContrastRatio } from '@mui/material/styles';
import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import _ from '@lodash';
import {
  defaultSettings,
  defaultThemeOptions,
  extendThemeWithMixins,
  getParsedQuerySettings,
  mustHaveThemeOptions,
} from '@fuse/default-settings';
import settingsConfig from 'app/configs/settingsConfig';
import themeLayoutConfigs from 'app/theme-layouts/themeLayoutConfigs';
import { getUser } from 'app/store/userSlice';
import themesConfig, { darkPaletteText, lightPaletteText } from 'app/configs/themesConfig';
import { resolveThemeMode } from '@/utils/themeUtils';

export const changeFuseTheme = (theme) => (dispatch, getState) => {
  const { fuse } = getState();
  const { settings } = fuse;

  // Respect the user's dark mode preference from localStorage
  const effectiveMode = resolveThemeMode();
  const applyDarkMode = effectiveMode === 'dark';

  // Apply dark/light mode transformation to themes if needed
  const mainTheme = applyDarkMode
    ? changeThemeMode(theme.main.theme, 'dark')
    : theme.main.theme;
  const toolbarTheme = applyDarkMode
    ? changeThemeMode(theme.toolbar.theme, 'dark')
    : theme.toolbar.theme;

  const newSettings = {
    ...settings,
    theme: {
      main: mainTheme,
      navbar: theme.navbar.theme,
      toolbar: toolbarTheme,
      footer: theme.footer.theme,
      orderTimeLine: theme.orderTimeLine,
    },
    logoUrl: theme.logoUrl,
    licenseeVaultLogo: theme.logoUrl,
  };

  dispatch(setDefaultSettings(newSettings));
};

function getInitialSettings() {
  const defaultLayoutStyle = settingsConfig?.layout?.style
    ? settingsConfig.layout.style
    : 'layout1';
  const layout = {
    style: defaultLayoutStyle,
    config: themeLayoutConfigs[defaultLayoutStyle].defaults,
  };

  // Resolve theme mode from localStorage or system preference
  const effectiveMode = resolveThemeMode();
  const themeForMode = effectiveMode === 'dark' ? themesConfig.defaultDark : themesConfig.default;

  // Override the default theme settings with resolved theme
  const themeOverride = {
    theme: {
      main: themeForMode,
      navbar: themesConfig.defaultDark, // Keep navbar dark
      toolbar: themeForMode,
      footer: themesConfig.defaultDark, // Keep footer dark
    },
  };

  return _.merge(
    {},
    defaultSettings,
    { layout },
    settingsConfig,
    themeOverride, // Apply theme override
    getParsedQuerySettings(),
  );
}

export function generateSettings(_defaultSettings, _newSettings) {
  return _.merge(
    {},
    _defaultSettings,
    { layout: { config: themeLayoutConfigs[_newSettings?.layout?.style]?.defaults } },
    _newSettings,
  );
}

const initialSettings = getInitialSettings();

const initialState = {
  initial: initialSettings,
  defaults: _.merge({}, initialSettings),
  current: _.merge({}, initialSettings),
};

export const setDefaultSettings = createAsyncThunk(
  'fuse/settings/setDefaultSettings',
  async (val, { getState }) => {
    const { fuse } = getState();
    const { settings } = fuse;
    const defaults = generateSettings(settings.defaults, val);

    return {
      ...settings,
      defaults: _.merge({}, defaults),
      current: _.merge({}, defaults),
    };
  },
);

export const setInitSettings = createAsyncThunk(
  'fuse/settings/setInitSettings',
  async (val, { dispatch }) => {
    dispatch(setInitialSettings());
  },
);

const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    setSettings: (state, action) => {
      const current = generateSettings(state.defaults, action.payload);

      return {
        ...state,
        current,
      };
    },

    setInitialSettings: () => {
      return _.merge({}, initialState);
    },
    resetSettings: (state) => {
      return {
        ...state,
        defaults: _.merge({}, state.defaults),
        current: _.merge({}, state.defaults),
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(setDefaultSettings.fulfilled, (state, action) => action.payload);
    builder.addCase(getUser.fulfilled, (state, action) => {
      const defaults = generateSettings(state.defaults, action.payload?.data?.settings);

      return {
        ...state,
        defaults: _.merge({}, defaults),
        current: _.merge({}, defaults),
      };
    });
  },
});

const getDirection = (state) => state.fuse.settings.current.direction;
const getMainTheme = (state) => state.fuse.settings.current.theme.main;
const getNavbarTheme = (state) => state.fuse.settings.current.theme.navbar;
const getToolbarTheme = (state) => state.fuse.settings.current.theme.toolbar;
const getFooterTheme = (state) => state.fuse.settings.current.theme.footer;

const getOrderTimeLineTheme = (state) => {
  return state.fuse.settings.current.theme.orderTimeLine;
};

function generateMuiTheme(theme, direction) {
  const data = _.merge({}, defaultThemeOptions, theme, mustHaveThemeOptions);
  return createTheme(
    _.merge({}, data, {
      mixins: extendThemeWithMixins(data),
      direction,
    }),
  );
}

export const selectContrastMainTheme = (bgColor) => {
  function isDark(color) {
    return getContrastRatio(color, '#ffffff') >= 3;
  }

  return isDark(bgColor) ? selectMainThemeDark : selectMainThemeLight;
};

function changeThemeMode(theme, mode) {
  // Use colors consistent with themesConfig.defaultDark and themesConfig.default
  const modes = {
    dark: {
      palette: {
        mode: 'dark',
        divider: 'rgba(241,245,249,.12)',
        background: {
          paper: '#1e293b',
          default: '#111827',
        },
        text: darkPaletteText,
      },
    },
    light: {
      palette: {
        mode: 'light',
        divider: '#e2e8f0',
        background: {
          paper: '#FFFFFF',
          default: '#f1f5f9',
        },
        text: lightPaletteText,
      },
    },
  };

  return _.merge({}, theme, modes[mode]);
}

export const selectMainTheme = createSelector([getMainTheme, getDirection], (theme, direction) =>
  generateMuiTheme(theme, direction),
);

export const selectMainThemeDark = createSelector(
  [getMainTheme, getDirection],
  (theme, direction) => generateMuiTheme(changeThemeMode(theme, 'dark'), direction),
);

export const selectMainThemeLight = createSelector(
  [getMainTheme, getDirection],
  (theme, direction) => generateMuiTheme(changeThemeMode(theme, 'light'), direction),
);

export const selectNavbarTheme = createSelector(
  [getNavbarTheme, getDirection],
  (theme, direction) => generateMuiTheme(theme, direction),
);

export const selectNavbarThemeDark = createSelector(
  [getNavbarTheme, getDirection],
  (theme, direction) => generateMuiTheme(changeThemeMode(theme, 'dark'), direction),
);

export const selectNavbarThemeLight = createSelector(
  [getNavbarTheme, getDirection],
  (theme, direction) => generateMuiTheme(changeThemeMode(theme, 'light'), direction),
);

export const selectToolbarTheme = createSelector(
  [getToolbarTheme, getDirection],
  (theme, direction) => generateMuiTheme(theme, direction),
);

export const selectOrderTimeLineTheme = createSelector([getOrderTimeLineTheme], (theme) => theme);

export const selectToolbarThemeDark = createSelector(
  [getToolbarTheme, getDirection],
  (theme, direction) => generateMuiTheme(changeThemeMode(theme, 'dark'), direction),
);

export const selectToolbarThemeLight = createSelector(
  [getToolbarTheme, getDirection],
  (theme, direction) => generateMuiTheme(changeThemeMode(theme, 'light'), direction),
);

export const selectFooterTheme = createSelector(
  [getFooterTheme, getDirection],
  (theme, direction) => generateMuiTheme(theme, direction),
);

export const selectFooterThemeDark = createSelector(
  [getFooterTheme, getDirection],
  (theme, direction) => generateMuiTheme(changeThemeMode(theme, 'dark'), direction),
);

export const selectFooterThemeLight = createSelector(
  [getFooterTheme, getDirection],
  (theme, direction) => generateMuiTheme(changeThemeMode(theme, 'light'), direction),
);

export const selectFuseCurrentSettings = ({ fuse }) => fuse.settings.current;

export const selectFuseCurrentLayoutConfig = ({ fuse }) => fuse.settings.current.layout.config;

export const selectFuseDefaultSettings = ({ fuse }) => fuse.settings.defaults;

export const selectFuseThemesSettings = ({ fuse }) => fuse.settings.themes;

export const selectLogo = ({ fuse }) => fuse.settings.current.logoUrl;

export const { resetSettings, setInitialSettings, setSettings } = settingsSlice.actions;

export default settingsSlice.reducer;
